import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  useToast,
} from '@adop/ui';
import { CreditCard } from 'lucide-react';
import type {
  BillingCheckoutResponse,
  BillingPlanOption,
  BillingPortalResponse,
  BillingStatusResponse,
} from '@adop/shared';
import { planInfo } from '@adop/shared/plans';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import { apiFetch, postJson, errorMessage } from '../../lib/api';
import { planActionReason, planOptions, planStatusVariant } from '../../lib/billing';
import { formatDate, humanize } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';

const STATUS_PATH = '/api/platform/billing/status';
const CHECKOUT_PATH = '/api/platform/billing/checkout';
const PORTAL_PATH = '/api/platform/billing/portal';

export interface BillingPageProps {
  /** Real browser navigation by default; tests inject a spy. */
  redirect?: (url: string) => void;
}

function defaultRedirect(url: string): void {
  window.location.assign(url);
}

/** Billing endpoints act on an explicit restaurant; never let the server guess. */
function billingPath(path: string, restaurantId: string): string {
  return `${path}?restaurant_id=${encodeURIComponent(restaurantId)}`;
}

export function BillingPage({ redirect = defaultRedirect }: BillingPageProps) {
  const { restaurant, canOwn } = useWorkspace();
  const { toast } = useToast();
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const state = useAsync(
    () =>
      restaurant
        ? apiFetch<BillingStatusResponse>(billingPath(STATUS_PATH, restaurant.id))
        : Promise.resolve(null),
    [restaurant?.id],
  );

  if (!restaurant) {
    return (
      <EmptyState
        icon={<CreditCard className="h-6 w-6" />}
        title="No restaurant selected"
        description="Choose a restaurant to see its plan and billing."
      />
    );
  }

  const status = state.data;
  const plans = status ? planOptions(status) : [];

  const startCheckout = async (plan: BillingPlanOption) => {
    setActionError(null);
    setBusyPlan(plan.id);
    try {
      const response = await postJson<BillingCheckoutResponse>(
        billingPath(CHECKOUT_PATH, restaurant.id),
        { plan: plan.id },
      );
      redirect(response.url);
    } catch (cause) {
      const message = errorMessage(cause);
      setActionError(message);
      toast({ title: 'Could not start checkout', description: message, variant: 'error' });
    } finally {
      setBusyPlan(null);
    }
  };

  const openPortal = async () => {
    setActionError(null);
    setPortalBusy(true);
    try {
      const response = await postJson<BillingPortalResponse>(billingPath(PORTAL_PATH, restaurant.id));
      redirect(response.url);
    } catch (cause) {
      const message = errorMessage(cause);
      setActionError(message);
      toast({ title: 'Could not open billing portal', description: message, variant: 'error' });
    } finally {
      setPortalBusy(false);
    }
  };

  const currentInfo = status ? planInfo(status.plan) : null;
  const manageReason = !status
    ? null
    : !status.configured
      ? 'Billing is not configured yet.'
      : !canOwn
        ? 'Only the owner can manage billing.'
        : null;

  return (
    <div className="space-y-5">
      <PageHeader title="Billing" description="Your adop plan and subscription." />

      {actionError ? <Alert>{actionError}</Alert> : null}
      {state.error ? <Alert>{state.error}</Alert> : null}

      {state.loading ? (
        <LoadingBlock />
      ) : status ? (
        <>
          {!status.configured ? (
            <Alert>
              <p className="font-medium">Billing is not available yet.</p>
              <p className="mt-1">
                The server is missing:{' '}
                <strong>{status.missing.length > 0 ? status.missing.join(', ') : 'unknown'}</strong>. This
                is a setup issue on our side, not a problem with your account.
              </p>
            </Alert>
          ) : null}

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle>Current plan</CardTitle>
                {status.plan_status ? (
                  <Badge variant={planStatusVariant(status.plan_status)}>
                    {humanize(status.plan_status)}
                  </Badge>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-lg font-semibold text-zinc-900">
                  {currentInfo?.label ?? status.plan}
                </p>
                {currentInfo ? (
                  <p className="text-sm text-zinc-600">{currentInfo.blurb}</p>
                ) : (
                  <p className="text-sm text-zinc-500">
                    We don&apos;t recognise the “{status.plan}” plan, so there is nothing to show about it.
                  </p>
                )}
                {status.plan_renews_at ? (
                  <p className="mt-1 text-sm text-zinc-500">
                    Renews {formatDate(status.plan_renews_at)}
                  </p>
                ) : null}
              </div>

              {status.has_customer ? (
                <div className="space-y-1">
                  <Button
                    variant="outline"
                    loading={portalBusy}
                    disabled={manageReason !== null}
                    onClick={() => void openPortal()}
                  >
                    Manage billing
                  </Button>
                  {manageReason ? <p className="text-xs text-zinc-500">{manageReason}</p> : null}
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Plans</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {plans.map((plan) => {
                const info = planInfo(plan.id);
                const isCurrent = plan.id === status.plan;
                const reason = isCurrent
                  ? null
                  : planActionReason({ configured: status.configured, canOwn, plan });
                return (
                  <div
                    key={plan.id}
                    className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-zinc-900">{plan.label}</p>
                        {isCurrent ? <Badge variant="success">Current plan</Badge> : null}
                      </div>
                      {info ? <p className="text-sm text-zinc-500">{info.blurb}</p> : null}
                      {info ? (
                        <p className="mt-0.5 text-xs text-zinc-400">
                          {info.includedOrdersPerMonth === null
                            ? 'Unlimited orders'
                            : `${info.includedOrdersPerMonth} orders included / month`}
                        </p>
                      ) : null}
                    </div>
                    {isCurrent ? null : (
                      <div className="sm:text-right">
                        <Button
                          size="sm"
                          loading={busyPlan === plan.id}
                          disabled={reason !== null}
                          onClick={() => void startCheckout(plan)}
                        >
                          Upgrade
                        </Button>
                        {reason ? (
                          <p className="mt-1 max-w-[16rem] text-xs text-zinc-500 sm:ml-auto">
                            {reason}
                          </p>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
